/**
 * TanStack Query hooks for the event permalink page.
 *
 * Query key conventions:
 *   ['alert-detail', alertId]   - full alert detail (5m stale; event data is immutable)
 *   ['alert-related', alertId]  - related events list (5m stale; immutable)
 *
 * Both hooks are disabled when alertId is 0 (invalid / not yet parsed).
 * The EventPage component validates the :id param before passing it here.
 */
import { useQuery } from '@tanstack/react-query';
import { fetchAlertDetail, fetchAlertRelated, fetchEntryEventDetail } from './api';

/**
 * Fetches full alert detail for the given alert ID.
 *
 * Disabled when alertId is 0. Returns null data on 404 (cross-org or
 * non-existent). The EventPage renders the 404 empty state in that case.
 *
 * Stale time is 5 minutes: alert records are immutable after resolution,
 * so fetching more than once per session is unnecessary.
 *
 * @param alertId - Numeric primary key parsed from /event/:id URL param
 */
export function useAlertDetail(alertId: number) {
  return useQuery({
    queryKey: ['alert-detail', alertId],
    queryFn: () => fetchAlertDetail(alertId),
    enabled: alertId > 0,
    staleTime: 5 * 60_000,
  });
}

/**
 * Fetches related events for the given alert ID.
 *
 * Returns [] when no related events exist or alertId is invalid.
 * Runs in parallel with useAlertDetail on EventPage mount.
 *
 * @param alertId - Numeric primary key parsed from /event/:id URL param
 */
export function useAlertRelated(alertId: number) {
  return useQuery({
    queryKey: ['alert-related', alertId],
    queryFn: () => fetchAlertRelated(alertId),
    enabled: alertId > 0,
    staleTime: 5 * 60_000,
  });
}

/**
 * Fetches one entry-event detail row for the EntryEventDrill page.
 *
 * Plan 12-11 consumer of GET /v1/entry-events/{event_id}. Disabled when
 * `eventId` is null or zero; returns null data on 404 so the EventPage
 * branch can render the not-found empty state cleanly.
 *
 * @param eventId - BigInt id parsed from the URL, or null to disable
 */
export function useEntryEventDetail(eventId: number | null) {
  return useQuery({
    queryKey: ['entry-event-detail', eventId],
    queryFn: () => fetchEntryEventDetail(eventId!),
    enabled: Boolean(eventId && eventId > 0),
    // Entry events are immutable after they fire; 5 minutes matches
    // the alert-detail cache TTL.
    staleTime: 5 * 60_000,
  });
}
